// 👥 Employés E2 — Bloc « Statut » : statut d'emploi (fiche) et statut d'accès (compte), boutons contextuels, historique.
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { HelpCircle, History, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-CA') : '…');

export const LIBELLES_ACCES = {
  actif: { label: 'Actif', cls: 'bg-green-100 text-green-700' },
  suspendu_auto: { label: 'Suspendu automatiquement (fiche inactive)', cls: 'bg-orange-100 text-orange-700' },
  suspendu_manuel: { label: 'Suspendu manuellement', cls: 'bg-red-100 text-red-700' },
  invitation_en_attente: { label: 'Invitation en attente', cls: 'bg-blue-100 text-blue-700' },
  aucun_compte: { label: 'Aucun compte', cls: 'bg-slate-100 text-slate-600' },
};

export function texteAcces(acces) {
  if (!acces) return '…';
  const base = LIBELLES_ACCES[acces.statut]?.label || acces.statut;
  if (acces.statut === 'suspendu_manuel') return `${base} par ${acces.par || '—'} le ${fmtDate(acces.date)}`;
  if (acces.statut === 'invitation_en_attente') return `${base} (expire le ${fmtDate(acces.date)})`;
  return base;
}

function Aide({ texte, testid }) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="text-slate-400 hover:text-slate-600" aria-label="Aide" data-testid={testid}>
            <HelpCircle className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{texte}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function DialogueConfirmation({ etat, onClose, onConfirm, saving }) {
  const [motif, setMotif] = useState('');
  if (!etat) return null;
  const { titre, consequences, avertissement, avecMotif, libelleBouton, variant } = etat;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialogue-statut">
        <DialogHeader>
          <DialogTitle>{titre}</DialogTitle>
          <DialogDescription>Voici ce qui se passera :</DialogDescription>
        </DialogHeader>
        <ul className="list-disc pl-5 text-sm space-y-1" data-testid="dialogue-statut-consequences">
          {consequences.map((c) => <li key={c}>{c}</li>)}
        </ul>
        {avertissement && <p className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded p-2" data-testid="dialogue-statut-avertissement">{avertissement}</p>}
        {avecMotif && (
          <Textarea value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Motif (facultatif)" rows={2} data-testid="dialogue-statut-motif" />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dialogue-statut-annuler">Annuler</Button>
          <Button variant={variant || 'default'} disabled={saving} onClick={() => onConfirm(motif)} data-testid="dialogue-statut-confirmer">{libelleBouton}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BlocStatutEmploye({ employee, isProprietaire, onChanged, onInviter }) {
  const [acces, setAcces] = useState(employee?.acces || null);
  const [historique, setHistorique] = useState([]);
  const [ouvert, setOuvert] = useState(false);
  const [dialogue, setDialogue] = useState(null);
  const [saving, setSaving] = useState(false);
  const estActif = employee?.est_actif !== false;

  const charger = useCallback(async () => {
    if (!employee?.id) return;
    try {
      const [f, h] = await Promise.all([
        axios.get(`${API_URL}/api/employees/${employee.id}`, { headers: headers() }),
        isProprietaire ? axios.get(`${API_URL}/api/employees/${employee.id}/statut-historique`, { headers: headers() }) : Promise.resolve({ data: { historique: [] } }),
      ]);
      setAcces(f.data?.acces || null);
      setHistorique(h.data?.historique || []);
    } catch { /* lecture seule : silencieux */ }
  }, [employee?.id, isProprietaire]);

  useEffect(() => { charger(); }, [charger]);

  const appliquer = async (url, body, succes) => {
    setSaving(true);
    try {
      const { data } = await axios.post(url, body, { headers: headers() });
      toast.success(succes);
      (data.avertissements || []).forEach((a) => toast.warning(a, { duration: 10000 }));
      setAcces(data.acces || null);
      setDialogue(null);
      if (data.employee) onChanged?.(data.employee);
      charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally { setSaving(false); }
  };

  const ouvrirInactif = () => setDialogue({
    titre: `Mettre ${employee.prenom} inactif ?`, avecMotif: true, libelleBouton: 'Mettre inactif', variant: 'destructive',
    consequences: ['L\'accès à la plateforme sera suspendu automatiquement (sauf si une autre fiche active de la même personne existe).', 'Ses sessions ouvertes seront fermées.', 'Tout l\'historique (heures, documents, évaluations) est conservé.'],
    onConfirm: (motif) => appliquer(`${API_URL}/api/employees/${employee.id}/statut-emploi`, { est_actif: false, motif: motif || null }, `${employee.prenom} mis inactif`),
  });
  const ouvrirReactiver = () => setDialogue({
    titre: `Réactiver ${employee.prenom} ?`, avecMotif: true, libelleBouton: 'Réactiver',
    consequences: ['La fiche redevient active (planification, pointage, kiosque entrepôt).', acces?.statut === 'suspendu_auto' ? 'L\'accès à la plateforme sera rétabli.' : 'L\'accès à la plateforme n\'est pas modifié.'],
    avertissement: acces?.statut === 'suspendu_manuel' ? 'L\'accès a été suspendu manuellement : il restera suspendu. Utilisez « Rétablir l\'accès » si souhaité.' : null,
    onConfirm: (motif) => appliquer(`${API_URL}/api/employees/${employee.id}/statut-emploi`, { est_actif: true, motif: motif || null }, `${employee.prenom} réactivé`),
  });
  const ouvrirAcces = (action) => setDialogue({
    titre: action === 'suspendre' ? 'Suspendre l\'accès à la plateforme ?' : 'Rétablir l\'accès à la plateforme ?', avecMotif: true,
    libelleBouton: action === 'suspendre' ? 'Suspendre l\'accès' : 'Rétablir l\'accès', variant: action === 'suspendre' ? 'destructive' : 'default',
    consequences: action === 'suspendre'
      ? ['La personne ne pourra plus se connecter ; ses sessions seront fermées.', 'La fiche employé reste telle quelle (statut d\'emploi inchangé).']
      : ['La personne pourra de nouveau se connecter avec ses identifiants.', estActif ? 'Le kiosque entrepôt sera accessible.' : 'La fiche est inactive : le kiosque entrepôt restera refusé.'],
    onConfirm: (motif) => appliquer(`${API_URL}/api/employees/${employee.id}/acces`, { action, motif: motif || null }, action === 'suspendre' ? 'Accès suspendu' : 'Accès rétabli'),
  });

  const a = LIBELLES_ACCES[acces?.statut] || LIBELLES_ACCES.aucun_compte;
  return (
    <Card className="border-slate-200" data-testid="bloc-statut-employe">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-sm" data-testid="statut-emploi-ligne">
            <span className="text-slate-500">Statut d'emploi :</span>
            <Badge className={estActif ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} data-testid="statut-emploi-badge">{estActif ? 'Actif' : 'Inactif'}</Badge>
            {!estActif && (
              <span className="text-slate-600">depuis le {fmtDate(employee.statut_emploi_modifie_a)} par {employee.statut_emploi_modifie_par || '—'}{employee.statut_emploi_motif ? ` (${employee.statut_emploi_motif})` : ''}</span>
            )}
            <Aide testid="aide-statut-emploi" texte="Le statut d'emploi dit si la personne travaille encore pour l'entreprise ; un inactif conserve tout son historique." />
          </div>
          {isProprietaire && (
            estActif
              ? <Button size="sm" variant="outline" className="border-orange-500 text-orange-600 hover:bg-orange-50" onClick={ouvrirInactif} data-testid="btn-mettre-inactif">Mettre inactif</Button>
              : <Button size="sm" variant="outline" className="border-green-600 text-green-700 hover:bg-green-50" onClick={ouvrirReactiver} data-testid="btn-reactiver-fiche">Réactiver</Button>
          )}
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-sm" data-testid="statut-acces-ligne">
            <span className="text-slate-500">Accès à la plateforme :</span>
            <Badge className={a.cls} data-testid="statut-acces-badge">{texteAcces(acces)}</Badge>
            <Aide testid="aide-statut-acces" texte="L'accès à la plateforme dit si la personne peut se connecter avec son compte ; il est suspendu automatiquement quand la fiche est inactive." />
          </div>
          {isProprietaire && acces && (
            <div className="flex gap-2">
              {acces.statut === 'aucun_compte' && <Button size="sm" variant="outline" onClick={() => onInviter?.(employee)} data-testid="btn-inviter-plateforme">Inviter sur la plateforme</Button>}
              {(acces.statut === 'actif' || acces.statut === 'invitation_en_attente') && <Button size="sm" variant="outline" className="border-red-500 text-red-600 hover:bg-red-50" onClick={() => ouvrirAcces('suspendre')} data-testid="btn-suspendre-acces">Suspendre l'accès</Button>}
              {(acces.statut === 'suspendu_manuel' || acces.statut === 'suspendu_auto') && <Button size="sm" variant="outline" className="border-green-600 text-green-700 hover:bg-green-50" onClick={() => ouvrirAcces('retablir')} data-testid="btn-retablir-acces">Rétablir l'accès</Button>}
            </div>
          )}
        </div>
        {isProprietaire && (
          <Collapsible open={ouvert} onOpenChange={setOuvert}>
            <CollapsibleTrigger asChild>
              <button type="button" className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" data-testid="toggle-historique-statuts">
                <History className="w-3.5 h-3.5" /> Historique des statuts ({historique.length}) <ChevronDown className={`w-3.5 h-3.5 transition-transform ${ouvert ? 'rotate-180' : ''}`} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul className="mt-2 space-y-1 text-xs text-slate-600" data-testid="historique-statuts">
                {historique.length === 0 && <li>Aucune transition enregistrée.</li>}
                {historique.map((h, i) => (
                  <li key={`${h.date}-${i}`}>
                    {fmtDate(h.date)} · {h.type === 'emploi' ? 'Emploi' : 'Accès'} : {h.ancien || '—'} → {h.nouveau || h.action || '—'} · par {h.par || '—'}{h.motif ? ` · ${h.motif}` : ''}{h.raison ? ` (${h.raison})` : ''}
                  </li>
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
      <DialogueConfirmation etat={dialogue} onClose={() => setDialogue(null)} onConfirm={(m) => dialogue.onConfirm(m)} saving={saving} />
    </Card>
  );
}
